import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getStockTransfers, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-600",
  completed: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
};

export default function StockTransfers() {
  const { t } = useTranslation("stock_transfers");

  const STATUS_OPTIONS = [
    { value: "", label: t("status_option_all", "All statuses") },
    { value: "pending", label: t("status_pending", "Pending") },
    { value: "completed", label: t("status_completed", "Completed") },
    { value: "cancelled", label: t("status_cancelled", "Cancelled") },
  ];

  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [fromBranchFilter, setFromBranchFilter] = useState("");
  const [toBranchFilter, setToBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchTransfers = async () => {
    try {
      const params = {};
      if (fromBranchFilter) params.from_branch_id = fromBranchFilter;
      if (toBranchFilter) params.to_branch_id = toBranchFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getStockTransfers(token, params);
      setTransfers(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchTransfers(); }, [fromBranchFilter, toBranchFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fromBranchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];
  const toBranchOptions = fromBranchOptions;

  const sortedAndFiltered = useMemo(() => {
    let result = [...transfers];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [transfers, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    { key: "code", label: t("col_code", "Code"), className: "text-gray-700 font-mono font-bold" },
    { key: "from_branch", label: t("col_from_branch", "From"), className: "text-gray-600", render: (row) => row.from_branch?.name || `#${row.from_branch_id}` },
    { key: "to_branch", label: t("col_to_branch", "To"), className: "text-gray-600", render: (row) => row.to_branch?.name || `#${row.to_branch_id}` },
    { key: "created_at", label: t("col_date", "Date"), className: "text-gray-500", render: (row) => new Date(row.created_at).toLocaleString() },
    {
      key: "status",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-500"}`}>
          {t(`status_${row.status}`, row.status)}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.StockTransfers size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Stock Transfers")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Move stock from one branch to another")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-stock-transfer")} className="w-full sm:w-auto shrink-0">
          {t("btn_add_stock_transfer", "New Stock Transfer")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SelectBox label={t("filter_from_branch_label", "Filter by source branch")} options={fromBranchOptions} value={fromBranchFilter} onChange={(val) => { setFromBranchFilter(val); setCurrentPage(1); }} />
        <SelectBox label={t("filter_to_branch_label", "Filter by destination branch")} options={toBranchOptions} value={toBranchFilter} onChange={(val) => { setToBranchFilter(val); setCurrentPage(1); }} />
        <SelectBox label={t("filter_status_label", "Filter by status")} options={STATUS_OPTIONS} value={statusFilter} onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <Button variant="outline" size="sm" icon={ICONS.StockTransfers} onClick={() => navigate(`/stock-transfers/${row.id}`)}>{isMobile ? ` ${t("btn_view", "View")}` : ""}</Button>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.code}</span>
            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-500"}`}>
              {t(`status_${row.status}`, row.status)}
            </span>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
